<template>
  <div>
    <div class="page-head">
      <h1>Profile</h1>
      <p>Your account information, Pass purchase history, and password.</p>
    </div>
    <dsa-loading v-if="loading" text="Loading profile…" />
    <template v-else>
      <div class="card" style="max-width:640px;margin-bottom:24px">
        <div class="section-head" style="margin-bottom:16px"><h2>Account</h2></div>
        <div class="field">
          <div class="label">Given name (EN)</div>
          <input v-model="form.student_givenName_pinyin" class="input" :disabled="!editing">
        </div>
        <div class="field">
          <div class="label">Family name (EN)</div>
          <input v-model="form.student_lastName_pinyin" class="input" :disabled="!editing">
        </div>
        <div class="field">
          <div class="label">Email</div>
          <input v-model="form.student_email" class="input" :disabled="!editing">
        </div>
        <div class="field">
          <div class="label">Account type</div>
          <div>{{ student.is_free_student ? 'Free student' : 'Institution student' }}</div>
        </div>
        <div v-if="student.account_name" class="field">
          <div class="label">Institution</div>
          <div>{{ student.account_name }}</div>
        </div>
        <div class="actions" style="margin-top:20px">
          <button v-if="!editing" type="button" class="btn btn-outline" @click="editing = true">Edit</button>
          <template v-else>
            <button type="button" class="btn btn-outline" :disabled="saving" @click="cancelEdit">Cancel</button>
            <button type="button" class="btn btn-primary" :disabled="saving" @click="save">{{ saving ? 'Saving…' : 'Save' }}</button>
          </template>
        </div>
      </div>

      <div class="card" style="max-width:640px;margin-bottom:24px">
        <div class="section-head" style="margin-bottom:16px"><h2>Change password</h2></div>
        <p class="hint" style="margin-bottom:14px">Used for password login if enabled for your account. PIN login is unchanged.</p>
        <div class="field">
          <div class="label">Current password</div>
          <input v-model="pwd.old_pwd" type="password" class="input" autocomplete="current-password">
        </div>
        <div class="field">
          <div class="label">New password</div>
          <input v-model="pwd.new_pwd" type="password" class="input" autocomplete="new-password">
        </div>
        <div class="field">
          <div class="label">Confirm new password</div>
          <input v-model="pwd.confirm" type="password" class="input" autocomplete="new-password">
        </div>
        <button type="button" class="btn btn-primary" :disabled="pwdSaving" @click="savePassword">
          {{ pwdSaving ? 'Updating…' : 'Update password' }}
        </button>
      </div>

      <div class="section" style="margin-top:0;max-width:960px">
        <div class="section-head">
          <h2>Pass purchase history</h2>
          <router-link to="/student/pass" class="entry">Buy / wallet</router-link>
        </div>
        <div v-if="!orders.length" class="empty" style="padding:28px 24px">
          <h3>No Pass orders yet</h3>
          <p style="margin-bottom:0">Purchase a DSA Pass to unlock exam attempts.</p>
        </div>
        <table v-else>
          <thead>
            <tr>
              <th>Pass</th>
              <th>Status</th>
              <th>Amount</th>
              <th>Created</th>
              <th>Paid</th>
              <th style="text-align:right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="o in orders" :key="o.id">
              <td>{{ o.pass_name || 'DSA Pass' }}</td>
              <td>
                <span class="chip" :class="statusChip(o.status)">{{ orderStatus(o.status) }}</span>
              </td>
              <td>{{ (o.currency || 'USD') }} {{ o.pay_amount != null ? o.pay_amount : (o.total_price != null ? o.total_price : '—') }}</td>
              <td>{{ formatDate(o.create_time) }}</td>
              <td>{{ o.pay_time ? formatDate(o.pay_time) : '—' }}</td>
              <td class="td-actions" style="text-align:right">
                <span v-if="isUnpaidOrder(o)" class="action-row">
                  <button
                    type="button"
                    class="btn btn-sm btn-primary"
                    :disabled="cancellingId === o.id"
                    @click="payOrder(o)"
                  >Pay</button>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline"
                    :disabled="cancellingId === o.id"
                    @click="openCancel(o)"
                  >{{ cancellingId === o.id ? 'Cancelling…' : 'Cancel' }}</button>
                </span>
                <span
                  v-else-if="showRefundButton(o)"
                  class="refund-wrap"
                  :title="refundHover(o)"
                >
                  <button
                    type="button"
                    class="btn btn-sm"
                    :class="refundEnabled(o) ? 'btn-outline' : 'btn-disabled'"
                    :disabled="!refundEnabled(o) || refundingId === o.id"
                    @click="openRefund(o)"
                  >{{ refundingId === o.id ? 'Refunding…' : 'Refund' }}</button>
                </span>
                <span v-else class="nosh">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <div v-if="refundModal" class="modal-overlay" @click.self="refundModal = false">
      <div class="modal-card">
        <h3>Request refund?</h3>
        <p v-if="refundTarget">
          {{ refundConfirmText(refundTarget) }}
        </p>
        <div class="actions">
          <button type="button" class="btn btn-outline" :disabled="!!refundingId" @click="refundModal = false">Cancel</button>
          <button type="button" class="btn btn-primary" :disabled="!!refundingId" @click="confirmRefund">
            {{ refundingId ? 'Refunding…' : 'Confirm refund' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="cancelModal" class="modal-overlay" @click.self="cancelModal = false">
      <div class="modal-card">
        <h3>Cancel this order?</h3>
        <p>The unpaid Pass order will be marked as cancelled. You can buy a new Pass later.</p>
        <div class="actions">
          <button type="button" class="btn btn-outline" :disabled="!!cancellingId" @click="cancelModal = false">Keep order</button>
          <button type="button" class="btn btn-primary" :disabled="!!cancellingId" @click="confirmCancel">
            {{ cancellingId ? 'Cancelling…' : 'Confirm cancel' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getStudentInfo,
  updateStudentInfo,
  changePassword,
  getStudentPurchasdPasses,
  getPassWallet,
  requestDsaPassRefund,
  cancelDsaPassOrder,
} from '@/api/dsa'
import { getUserId, setUserName } from '@/utils/store'
import { studentDisplayName, unwrapId, formatDate } from '@/utils/dsaFormat'

const USED_HOVER = 'This Pass has been used and is not eligible for a refund.'

export default {
  name: 'StudentProfile',
  data() {
    return {
      loading: true,
      editing: false,
      saving: false,
      pwdSaving: false,
      student: {},
      orders: [],
      form: {
        student_givenName_pinyin: '',
        student_lastName_pinyin: '',
        student_email: '',
      },
      pwd: {
        old_pwd: '',
        new_pwd: '',
        confirm: '',
      },
      refundModal: false,
      refundTarget: null,
      refundingId: '',
      cancelModal: false,
      cancelTarget: null,
      cancellingId: '',
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    formatDate,
    orderStatus(status) {
      if (status === '已支付') return 'Paid'
      if (status === '未支付') return 'Unpaid'
      if (status === '已取消') return 'Cancelled'
      if (status === '已退款') return 'Refunded'
      if (status === '退款中') return 'Refund pending'
      return status || '—'
    },
    statusChip(status) {
      if (status === '已支付') return 'open'
      if (status === '已退款' || status === '已取消') return 'pend'
      return 'pend'
    },
    isUnpaidOrder(order) {
      return !!(order && order.status === '未支付')
    },
    refundInfo(order) {
      return (order && order.refund) || {}
    },
    showRefundButton(order) {
      return !!(order && order.status === '已支付')
    },
    refundEnabled(order) {
      const info = this.refundInfo(order)
      return !!(info.can_refund && info.button_enabled)
    },
    refundHover(order) {
      const info = this.refundInfo(order)
      if (info.hover_message) return info.hover_message
      if (info.has_exam_or_noshow || info.refund_tier === 'none') return USED_HOVER
      return ''
    },
    refundConfirmText(order) {
      const info = this.refundInfo(order)
      const currency = order.currency || info.currency || 'USD'
      const amount = info.refund_amount != null ? info.refund_amount : '—'
      const fee = info.service_fee != null ? info.service_fee : 0
      if (info.refund_tier === 'full') {
        return `This Pass has no subject registrations. You will receive a full refund of the registration fee (${currency} ${amount}). The payment service fee (${currency} ${fee}) is non-refundable.`
      }
      if (info.refund_tier === 'half') {
        return `This Pass has subject registration(s) but no exam or no-show records. You will receive 50% of the registration fee (${currency} ${amount}). The payment service fee (${currency} ${fee}) is non-refundable. Related registrations will be cancelled.`
      }
      return 'Confirm refund for this Pass?'
    },
    syncForm() {
      this.form = {
        student_givenName_pinyin: this.student.student_givenName_pinyin || this.student.student_givenName || '',
        student_lastName_pinyin: this.student.student_lastName_pinyin || this.student.student_lastName || '',
        student_email: this.student.student_email || '',
      }
    },
    cancelEdit() {
      this.editing = false
      this.syncForm()
    },
    openRefund(order) {
      if (!this.refundEnabled(order)) return
      this.refundTarget = order
      this.refundModal = true
    },
    async confirmRefund() {
      if (!this.refundTarget) return
      const order = this.refundTarget
      this.refundingId = order.id
      try {
        const res = await requestDsaPassRefund(getUserId(), order.id)
        if (res.data && res.data.code === 0) {
          this.$emit('toast', 'Refund processed')
          this.refundModal = false
          this.refundTarget = null
          await this.load()
        } else {
          this.$emit('toast', (res.data && res.data.msg) || 'Refund failed')
        }
      } catch (e) {
        this.$emit('toast', 'Refund failed')
      } finally {
        this.refundingId = ''
      }
    },
    payOrder(order) {
      if (!this.isUnpaidOrder(order)) return
      const orderId = order.id || order._id
      this.$router.push({ path: '/student/pass/order', query: { order_id: orderId } })
    },
    openCancel(order) {
      if (!this.isUnpaidOrder(order)) return
      this.cancelTarget = order
      this.cancelModal = true
    },
    async confirmCancel() {
      if (!this.cancelTarget) return
      const order = this.cancelTarget
      const orderId = order.id || order._id
      this.cancellingId = orderId
      try {
        const res = await cancelDsaPassOrder(getUserId(), orderId)
        if (res.data && res.data.code === 0) {
          this.$emit('toast', 'Order cancelled')
          this.cancelModal = false
          this.cancelTarget = null
          await this.load()
        } else {
          this.$emit('toast', (res.data && res.data.msg) || 'Cancel failed')
        }
      } catch (e) {
        this.$emit('toast', 'Cancel failed')
      } finally {
        this.cancellingId = ''
      }
    },
    async load() {
      this.loading = true
      try {
        const [infoRes, orderRes, walletRes] = await Promise.all([
          getStudentInfo(getUserId()),
          getStudentPurchasdPasses(getUserId()),
          getPassWallet(getUserId()),
        ])
        if (infoRes.data && infoRes.data.code === 0) {
          this.student = (infoRes.data.data && infoRes.data.data.student_info) || {}
          this.syncForm()
        }
        let orders = []
        if (orderRes.data && orderRes.data.code === 0) {
          orders = orderRes.data.data || []
        }
        if (!orders.length && walletRes.data && walletRes.data.code === 0) {
          const wallet = walletRes.data.data || {}
          orders = (wallet.passes || []).map(p => ({
            id: p.order_id,
            pass_name: p.pass_name || 'DSA Pass',
            status: '已支付',
            currency: p.currency || 'USD',
            pay_amount: p.pay_amount,
            create_time: p.pay_time,
            pay_time: p.pay_time,
            refund: {
              can_refund: false,
              button_enabled: false,
              refund_tier: 'none',
              hover_message: '',
            },
          }))
        }
        this.orders = orders
      } catch (e) {
        this.$emit('toast', 'Failed to load purchase history')
      } finally {
        this.loading = false
      }
    },
    async save() {
      this.saving = true
      try {
        const student_id = unwrapId(this.student) || getUserId()
        const res = await updateStudentInfo({
          student_id,
          student_givenName_pinyin: this.form.student_givenName_pinyin,
          student_lastName_pinyin: this.form.student_lastName_pinyin,
          student_email: this.form.student_email,
        })
        if (res.data && res.data.code === 0) {
          this.student = res.data.data || this.student
          setUserName(studentDisplayName(this.student))
          this.editing = false
          this.$emit('toast', 'Profile updated')
        } else {
          this.$emit('toast', (res.data && res.data.msg) || 'Update failed')
        }
      } catch (e) {
        this.$emit('toast', 'Update failed')
      } finally {
        this.saving = false
      }
    },
    async savePassword() {
      if (!this.pwd.old_pwd || !this.pwd.new_pwd) {
        this.$emit('toast', 'Please fill all password fields')
        return
      }
      if (this.pwd.new_pwd !== this.pwd.confirm) {
        this.$emit('toast', 'New passwords do not match')
        return
      }
      if (this.pwd.new_pwd.length < 6) {
        this.$emit('toast', 'New password must be at least 6 characters')
        return
      }
      this.pwdSaving = true
      try {
        const res = await changePassword(getUserId(), this.pwd.old_pwd, this.pwd.new_pwd)
        if (res.data && res.data.code === 0) {
          this.$emit('toast', 'Password updated')
          this.pwd = { old_pwd: '', new_pwd: '', confirm: '' }
        } else {
          this.$emit('toast', (res.data && res.data.msg) || 'Password update failed')
        }
      } catch (e) {
        this.$emit('toast', 'Password update failed')
      } finally {
        this.pwdSaving = false
      }
    },
  },
}
</script>

<style scoped>
.field{margin-bottom:16px}
.field .label{font-size:13px;color:var(--muted);font-weight:500;margin-bottom:6px}
.input{width:100%;box-sizing:border-box;border:1px solid #cbd5e1;border-radius:10px;padding:10px 14px;font-size:14px;font-family:inherit}
.input:disabled{background:#f4f6f9;color:var(--ink);border-color:var(--line)}
.input:focus{outline:none;border-color:var(--blue)}
.hint{font-size:12.5px;color:var(--muted);line-height:1.45}
.nosh{color:var(--muted)}
.action-row{display:inline-flex;align-items:center;gap:8px}
.refund-wrap{display:inline-block}
.btn-disabled,
.btn.btn-disabled{
  background:#e8edf3!important;
  color:#94a3b8!important;
  border-color:#d7dee8!important;
  cursor:not-allowed;
  box-shadow:none;
}
.modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;z-index:1000}
.modal-card{background:#fff;border-radius:14px;padding:24px;max-width:440px;width:90%;box-shadow:0 12px 40px rgba(15,23,42,.16)}
.modal-card h3{font-size:18px;font-weight:700;margin:0 0 8px}
.modal-card p{font-size:14px;color:var(--muted,#64748b);margin:0 0 20px;line-height:1.5}
.modal-card .actions{display:flex;justify-content:flex-end;gap:10px}
</style>
